import { signal } from '@angular/core';
import { UUID } from '@finxone-platform/shared/base-types';
import {
  PaginatedResponse,
  SupportConversation,
  SupportMessagesResponse,
} from '@finxone-platform/shared/sys-config-types';
import { Observable, of } from 'rxjs';

export class MockSupportChatsService {
  public activeChatId = signal<string | null>('123');
  public isNewChat = signal<boolean>(false);
  public getSupportChats(): Observable<PaginatedResponse<SupportConversation>> {
    return of({
      result: [
        {
          id: new UUID().toString(),
          subject: 'Question about my account',
          status: 'open',
          createdBy: { id: new UUID().toString(), name: 'John Doe' },
          lastMessage: {
            id: new UUID().toString(),
            content: 'I need help with...',
            isAdmin: false,
            createdAt: '2026-03-27T10:00:00Z',
          },
          unreadCount: 3,
          createdAt: '2026-03-27T10:00:00Z',
          updatedAt: '2026-03-29T12:00:00Z',
        },
        {
          id: new UUID().toString(),
          subject: 'Support',
          status: 'closed',
          createdBy: { id: new UUID().toString(), name: 'John Doe' },
          lastMessage: {
            id: new UUID().toString(),
            content: 'I need help with...',
            isAdmin: false,
            createdAt: '2026-02-15T14:30:00Z',
          },
          unreadCount: 0,
          createdAt: '2026-02-15T14:30:00Z',
          updatedAt: '2026-02-16T09:15:00Z',
        },
        {
          id: new UUID().toString(),
          subject: 'Billing Inquiry',
          status: 'closed',
          createdBy: { id: new UUID().toString(), name: 'John Doe' },
          lastMessage: {
            id: new UUID().toString(),
            content: 'I need help with...',
            isAdmin: false,
            createdAt: '2026-01-10T11:45:00Z',
          },
          unreadCount: 0,
          createdAt: '2026-01-10T11:45:00Z',
          updatedAt: '2026-01-10T16:20:00Z',
        },
      ],
      page: 1,
      limit: 20,
      totalItems: 3,
      totalPages: 1,
    });
  }

  public getSupportChatMessages(chatId = new UUID().toString()): Observable<SupportMessagesResponse> {
    const baseTime = Date.now();
    return of({
      result: [
        {
          id: new UUID().toString(),
          threadId: chatId,
          senderId: '1',
          isAdmin: false,
          content: `Hello, I'm facing some issues with transfers. My transfer seems to be stuck.`,
          createdAt: new Date(baseTime - 3600000).toISOString(),
        },
        {
          id: new UUID().toString(),
          threadId: chatId,
          senderId: '2',
          isAdmin: true,
          content:
            "Hi there! I'm sorry to hear you're having trouble with transfers. I'd be happy to help you with this issue.",
          createdAt: new Date(baseTime - 3000000).toISOString(),
        },
        {
          id: new UUID().toString(),
          threadId: chatId,
          senderId: '1',
          isAdmin: false,
          content:
            "Thanks! I tried to transfer £500 to my friend yesterday, but the money hasn't reached their account yet.",
          createdAt: new Date(baseTime - 2400000).toISOString(),
        },
        {
          id: new UUID().toString(),
          threadId: chatId,
          senderId: '2',
          isAdmin: true,
          content:
            'I understand your concern. Let me check the status of that transfer for you. Could you please provide the transaction reference number?',
          createdAt: new Date(baseTime - 1800000).toISOString(),
        },
        {
          id: new UUID().toString(),
          threadId: chatId,
          senderId: '1',
          isAdmin: false,
          content: 'Sure, the reference number is TX123456789. I sent it to account ending in 4567.',
          createdAt: new Date(baseTime - 1200000).toISOString(),
        },
        {
          id: new UUID().toString(),
          threadId: chatId,
          senderId: '2',
          isAdmin: true,
          content:
            'Thank you for that information. I can see your transfer is currently being processed by our payment system. Sometimes transfers can take up to 2 business days to complete. The status shows it should be completed by tomorrow morning.',
          createdAt: new Date(baseTime - 600000).toISOString(),
        },
        {
          id: new UUID().toString(),
          threadId: chatId,
          senderId: '1',
          isAdmin: false,
          content:
            'Oh okay, that makes sense. Thanks for checking! Is there anything I need to do, or should I just wait?',
          createdAt: new Date(baseTime - 300000).toISOString(),
        },
        {
          id: new UUID().toString(),
          threadId: chatId,
          senderId: '2',
          isAdmin: true,
          content:
            "No action needed from your side at the moment. The transfer is processing normally. If you don't see the funds in your friend's account by tomorrow afternoon, please reach out to us again and we'll investigate further. Is there anything else I can help you with today?",
          createdAt: new Date(baseTime - 100000).toISOString(),
        },
      ],
      hasMore: false,
      nextCursor: '',
    });
  }

  public resetActiveChatId() {
    this.activeChatId.set('');
  }

  public setNewChatStatus(status: boolean) {
    this.isNewChat.set(status);
  }
}
